import type { Currency, PlanSku } from '@/api/v1/billing/plans'
import { PlanRecurrence, ProductFeatureType } from '@/api/v1/billing/plans'
import {
  compareSkuRank,
  coversVolume,
  getSkusForRecurrence,
  resolveTierSku,
  skuVolume,
  TIER_CONFIG,
  UNLIMITED_ENVELOPES,
} from '@/lib/plans'

export const FEATURE_KEYS = [
  'eSignatures',
  'apiWebhooks',
  'whiteLabelResale',
] as const

export type FeatureKey = (typeof FEATURE_KEYS)[number]

export type UsageProfile = 'uniform' | 'seasonal' | null

export const FEATURE_MIN_PLAN: Record<FeatureKey, string> = {
  eSignatures: ProductFeatureType.ONE,
  apiWebhooks: ProductFeatureType.PRO,
  whiteLabelResale: ProductFeatureType.MAX,
}

const PLAN_RANK: Record<string, number> = {
  [ProductFeatureType.ONE]: 1,
  [ProductFeatureType.PRO]: 2,
  [ProductFeatureType.MAX]: 3,
}

export interface RecommendedSku {
  feature: string
  tierName: string
  /** Monthly bucket of the recommended variant; -1 = unlimited. */
  volume: number
  monthly: PlanSku | null
  yearly: PlanSku | null
  currency: Currency
  /**
   * Envelopes per month the plan does NOT include and the customer would buy
   * as add-ons. Zero when the plan already covers the demand — which is the
   * usual case, since the ranking prefers a plan that does. Always monthly:
   * `getRecurrenceQuote` is what scales it to the period being bought.
   */
  additionalEnvelopes: number
  /** Cents per add-on envelope on the MONTHLY SKU, or 0 when none are needed. */
  additionalEnvelopePrice: number
}

/**
 * What the customer actually pays per month on this SKU at this demand: the
 * plan price plus the add-on envelopes it would take to reach the volume.
 * Returns null when the SKU cannot serve the demand at all — it neither
 * includes enough nor sells add-ons.
 *
 * This is what makes an unlimited variant win on its own merits: Starter 100
 * plus 60 add-ons costs more than Starter Unlimited, so the ladder crosses and
 * the cheaper plan is the bigger one.
 */
export function getMonthlyCostAtVolume(
  sku: PlanSku,
  desiredMonthlyVolume: number,
): number | null {
  // Unlimited demand first: coversVolume compares numbers, so the -1 sentinel
  // would read as "any finite plan covers it", and add-ons cannot bridge an
  // infinite gap anyway.
  if (desiredMonthlyVolume === UNLIMITED_ENVELOPES) {
    return skuVolume(sku) === UNLIMITED_ENVELOPES ? sku.price : null
  }
  if (coversVolume(sku, desiredMonthlyVolume)) return sku.price
  if (sku.additionalEnvelopePrice === null) return null
  const missing = desiredMonthlyVolume - skuVolume(sku)
  return sku.price + missing * sku.additionalEnvelopePrice
}

export interface RecurrenceQuote {
  /** What one period actually costs: plan price plus its add-on envelopes. */
  price: number
  /** Envelopes for that period — allowance plus add-ons; -1 = unlimited. */
  envelopes: number
  /** Add-on envelopes for that period, in the period's own units. */
  additionalEnvelopes: number
}

/**
 * What one recurrence of the recommendation really costs and really delivers.
 *
 * Both sides are per-period: a yearly SKU already states its allowance and its
 * price for the whole year, so the add-ons have to be annualised to match —
 * 400 extra envelopes a month is 4,800 a year. The add-on unit price comes
 * from that same SKU, never from the monthly one: yearly add-ons are sold at
 * their own (cheaper) rate, and pricing a year of extras at the monthly rate
 * would overcharge the quote.
 *
 * One number, not two, because the annual add-ons are charged upfront with the
 * base plan (owner, 2026-08-05) — the year's total really is a single bill.
 */
export function getRecurrenceQuote(
  recommended: RecommendedSku,
  recurrence: 'Monthly' | 'Yearly',
): RecurrenceQuote {
  const sku = recurrence === 'Yearly' ? recommended.yearly : recommended.monthly
  if (!sku) return { price: 0, envelopes: 0, additionalEnvelopes: 0 }

  if (sku.envelopesIncluded === UNLIMITED_ENVELOPES) {
    return {
      price: sku.price,
      envelopes: UNLIMITED_ENVELOPES,
      additionalEnvelopes: 0,
    }
  }

  const periods = recurrence === 'Yearly' ? 12 : 1
  const additionalEnvelopes = recommended.additionalEnvelopes * periods
  return {
    price: sku.price + additionalEnvelopes * (sku.additionalEnvelopePrice ?? 0),
    envelopes: sku.envelopesIncluded + additionalEnvelopes,
    additionalEnvelopes,
  }
}

/**
 * The cheapest SKU that clears both gates: the feature floor and the monthly
 * demand — where "cheapest" is the real monthly bill (plan + add-ons), not the
 * sticker price of a plan that would need topping up every month.
 */
export function getRecommendedSku(
  selectedFeatures: Array<FeatureKey>,
  desiredMonthlyVolume: number,
  skus: Array<PlanSku>,
): RecommendedSku | null {
  let minRank = 1
  for (const feature of selectedFeatures) {
    const rank = PLAN_RANK[FEATURE_MIN_PLAN[feature]] ?? 1
    if (rank > minRank) minRank = rank
  }

  const monthlySkus = getSkusForRecurrence(skus, PlanRecurrence.MONTHLY)
  const yearlySkus = getSkusForRecurrence(skus, PlanRecurrence.YEARLY)

  const candidates = monthlySkus
    .filter((sku) => (PLAN_RANK[sku.tier] ?? 0) >= minRank)
    .map((sku) => ({
      sku,
      cost: getMonthlyCostAtVolume(sku, desiredMonthlyVolume),
    }))
    .filter(
      (candidate): candidate is { sku: PlanSku; cost: number } =>
        candidate.cost !== null,
    )

  if (candidates.length === 0) return null

  const winner = candidates.reduce((best, current) => {
    if (current.cost !== best.cost)
      return current.cost < best.cost ? current : best
    // Same bill: prefer the plan that already includes the volume. Paying the
    // same money with the allowance built in beats topping up every month —
    // and it is what makes unlimited win its ties.
    const currentCovers = coversVolume(current.sku, desiredMonthlyVolume)
    const bestCovers = coversVolume(best.sku, desiredMonthlyVolume)
    if (currentCovers !== bestCovers) return currentCovers ? current : best
    // Neither covers: the bigger allowance leaves less riding on add-ons, so a
    // month above the estimate costs the customer less.
    if (!currentCovers && skuVolume(current.sku) !== skuVolume(best.sku)) {
      return skuVolume(current.sku) > skuVolume(best.sku) ? current : best
    }
    return compareSkuRank(current.sku, best.sku) < 0 ? current : best
  }).sku

  const volume = skuVolume(winner)
  const tier = TIER_CONFIG.find((cfg) => cfg.feature === winner.tier)
  // Sized against the monthly demand because the ranking above is monthly. The
  // annual plan bills its add-ons upfront with the base plan, not month by
  // month — that conversion lives in getRecurrenceQuote, which is the only
  // thing allowed to state a per-period total.
  const additionalEnvelopes =
    volume === UNLIMITED_ENVELOPES
      ? 0
      : Math.max(0, desiredMonthlyVolume - volume)

  return {
    feature: winner.tier,
    tierName: tier?.name ?? winner.tier,
    volume,
    monthly: winner,
    yearly: resolveTierSku(yearlySkus, winner.tier, volume),
    currency: winner.currency,
    additionalEnvelopes,
    additionalEnvelopePrice:
      additionalEnvelopes > 0 ? (winner.additionalEnvelopePrice ?? 0) : 0,
  }
}
